import { action, observable, makeObservable } from 'mobx';
import moment from 'moment';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { IOrderByCondition } from 'Views/Components/ModelCollection/ModelQuery';
import * as Models from 'Models/Entities';
import * as AttrUtils from 'Util/AttributeUtils';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';

export interface IRecurrenceEntityAttributes extends IModelAttributes {
	interval: number;
	recurrenceType: string;
	startDate: Date;
	time: Date;
	endDate: Date;
	daysOfWeek: string;
	weekOrdinals?: string | null;
	ferryTrips: Array<Models.FerryTripEntity | Models.IFerryTripEntityAttributes>;
}

@entity('RecurrenceEntity', 'Recurrence')
export default class RecurrenceEntity extends Model implements IRecurrenceEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<RecurrenceEntity, number>()
	public interval: number;

	@observable
	@attribute<RecurrenceEntity, string>()
	public recurrenceType: string;

	@observable
	@attribute<RecurrenceEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	public startDate: Date;

	@observable
	@attribute<RecurrenceEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseTime(a) })
	public time: Date;

	@observable
	@attribute<RecurrenceEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	public endDate: Date;

	@observable
	@attribute<RecurrenceEntity, string>()
	public daysOfWeek: string;

	/** Comma-separated week-of-month names for a monthly pattern; null for weekly. */
	@observable
	@attribute<RecurrenceEntity, string>()
	public weekOrdinals: string | null;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public ferryTrips: Models.FerryTripEntity[] = [];

	constructor(attributes?: Partial<IRecurrenceEntityAttributes>) {
		super(attributes);

		this.ferryTrips = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IRecurrenceEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.interval)) {
				this.interval = attributes.interval;
			}
			if (stringNotEmpty(attributes.recurrenceType)) {
				this.recurrenceType = attributes.recurrenceType;
			}

			if (stringNotEmpty(attributes.daysOfWeek)) {
				this.daysOfWeek = attributes.daysOfWeek;
			}

			if (attributes.weekOrdinals !== undefined) {
				this.weekOrdinals = attributes.weekOrdinals;
			}

			if (isNotNullOrUndefined(attributes.startDate)) {
				if (attributes.startDate === null) {
					this.startDate = attributes.startDate;
				} else {
					this.startDate = moment(attributes.startDate).toDate();
				}
			}

			if (attributes.time !== undefined) {
				if (attributes.time === null) {
					this.time = attributes.time;
				} else {
					this.time = moment(attributes.time, 'HH:mm:ss').toDate();
				}
			}

			if (attributes.endDate !== undefined) {
				if (attributes.endDate === null) {
					this.endDate = attributes.endDate;
				} else {
					this.endDate = moment(attributes.endDate).toDate();
				}
			}
		}
	}
}
